/*
 * Identidad de NPC por ID exacto de 06_monsters, separada de la foto, la ubicación, la función y el inventario.
 * - displayName: nombre de presentación cuando el catálogo lo trunca. El nombre original del catálogo se conserva
 *   y se muestra como "Nombre en el catálogo"; ambos funcionan como alias en el buscador.
 * - mergedInto: el staff confirmó que es el mismo NPC que otro ID; la guía publica una sola ficha.
 * - Los IDs listados aquí se clasifican como NPC por indicación del staff (nunca por nombre parcial, nivel ni foto).
 */

export type NpcIdentity = {
  id: number
  /** Nombre del índice/lámina, solo para el informe (no sustituye al del catálogo). */
  indexName: string
  displayName?: string
  aliases?: string[]
  mergedInto?: number
  reason: string
  addedOn: string
}

export const npcIdentities: NpcIdentity[] = [
  {
    id: 379,
    indexName: 'Natasha the Firecracker Merchant',
    displayName: 'Natasha the Firecracker Merchant',
    aliases: ['natasha', 'firecracker merchant', 'vendedora de fuegos artificiales'],
    reason: 'El catálogo trunca el nombre ("Merchan"). Diferencia de escritura, no prueba de imagen incorrecta; la comprobación visual se registra aparte.',
    addedOn: '11/10/2026',
  },
  {
    id: 577,
    indexName: 'Leina the General Goods Merchant',
    displayName: 'Leina the General Goods Merchant',
    aliases: ['leina', 'general goods merchant', 'vendedora de objetos generales'],
    reason: 'El catálogo trunca el nombre ("Merchan"). Diferencia de escritura, no prueba de imagen incorrecta; la comprobación visual se registra aparte.',
    addedOn: '11/10/2026',
  },
  {
    id: 234,
    indexName: 'Pet Trainer',
    mergedInto: 226,
    aliases: ['pet trainer', 'entrenador de mascotas'],
    reason: 'Confirmado por administración: Pet Trainer (234) y Trainer (226) son el mismo NPC. Ficha principal: Trainer (226). El registro 234 se conserva en los archivos y se interpreta como 226.',
    addedOn: '11/10/2026',
  },
  { id: 547, indexName: 'Market Union Member Julia', aliases: ['julia', 'market union'], reason: 'NPC pendiente de reconciliar del índice VANGUARD_NPC, indicado por el staff.', addedOn: '11/10/2026' },
  { id: 369, indexName: 'Osbourne', reason: 'NPC pendiente de reconciliar del índice VANGUARD_NPC, indicado por el staff.', addedOn: '11/10/2026' },
  { id: 370, indexName: 'Jerridon', reason: 'NPC pendiente de reconciliar del índice VANGUARD_NPC, indicado por el staff.', addedOn: '11/10/2026' },
  { id: 247, indexName: 'Guard', reason: 'NPC pendiente de reconciliar del índice VANGUARD_NPC, indicado por el staff. Mismo nombre que 249: cada ID se conserva por separado.', addedOn: '11/10/2026' },
  { id: 249, indexName: 'Guard', reason: 'NPC pendiente de reconciliar del índice VANGUARD_NPC, indicado por el staff. Mismo nombre que 247: cada ID se conserva por separado.', addedOn: '11/10/2026' },
]

/** IDs que el staff pidió reconciliar expresamente. */
export const NPC_RECONCILE_IDS = [234, 547, 369, 370, 247, 249]

const byId = new Map(npcIdentities.map((n) => [n.id, n]))
export const npcIdentity = (id: number) => byId.get(id)

/** ID de la ficha principal: 234 → 226; cualquier otro ID se devuelve tal cual. */
export const canonicalNpcId = (id: number) => byId.get(id)?.mergedInto ?? id

/** IDs unificados en esta ficha principal (p. ej. 226 → [234]). */
export const mergedIdsOf = (id: number) => npcIdentities.filter((n) => n.mergedInto === id).map((n) => n.id)

/** Nombre de presentación: el del índice si el catálogo lo trunca; si no, el del catálogo. */
export const npcDisplayName = (id: number, catalogName: string) => byId.get(id)?.displayName ?? catalogName

/** ¿Difieren solo por escritura/truncado? (p. ej. "Merchan" frente a "Merchant") */
export function isSpellingDifference(a: string, b: string): boolean {
  const n = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')
  const x = n(a)
  const y = n(b)
  if (!x || !y || x === y) return x === y
  return x.startsWith(y) || y.startsWith(x)
}
